import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { type UpdateMovePayload, useCurrentMove, useSelectedQuote } from '../../../../modules/moves'
import Form from '../../../Form'
import SubmitBar from '../SubmitBar'
import useValidationSchema from './validationSchema'
import { type AddressesFormValues } from './AddressesForm.type'
import { formatFormOutput, moveToFormData } from './formDataBuilder'
import { Row, Cell } from '../../../data'
import { mergeClassName } from '../../../../utils/mergeClassName'
import LocationFields from './LocationFields'
import Stops from './Stops'

type AddressesFormProps = {
  onSubmit: (data: UpdateMovePayload) => Promise<void> | void
  onCancel?: () => void
  wide?: boolean
}

const AddressesForm: React.FC<AddressesFormProps> = ({
  onCancel,
  onSubmit,
  wide = false,
}) => {
  const { t } = useTranslation()
  const move = useCurrentMove()
  const selectedQuote = useSelectedQuote()

  const specificAddresses = !!selectedQuote

  const schema = useValidationSchema({ specificAddresses })
  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: moveToFormData(move, { specificAddresses }),
  })

  const handleSubmit = async (values: AddressesFormValues) => { await onSubmit(formatFormOutput(values)) }

  return (
    <Form
      form={form}
      onSubmit={handleSubmit}
    >
      <Row className={mergeClassName(
        'gap-x-4',
        !wide && 'lg:flex-col',
      )}
      >

        { ['origin', 'destination'].map(address => {
          const isOrigin = address === 'origin'
          const isDestination = address === 'destination'

          return (
            <Cell key={address}>
              <h3 className="my-2 font-bold text-black">
                { isOrigin && t('components.move.addresses.movingFrom') }
                { isDestination && t('components.move.addresses.movingTo') }
              </h3>

              <LocationFields
                name={address}
                specificAddresses={specificAddresses}
                wide={wide}
              />

              { isOrigin && (
                <Stops
                  specificAddresses={specificAddresses}
                />
              ) }

            </Cell>
          )
        }) }

      </Row>

      <SubmitBar onCancel={onCancel} />
    </Form>
  )
}

export default AddressesForm
